import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';

/**
 * Read-only listing of `public.super_admin_audit`. Newest first, paginated
 * client-side (50 rows / page). Wave 2A only writes audit rows via the
 * `superAdminAction` edge function — real volume arrives once 2A.2 wires
 * impersonation and 2B wires plan-edit mutations.
 */

interface AuditRow {
  id: string;
  actor_user_id: string;
  action: string;
  target_table: string | null;
  target_id: string | null;
  payload: unknown;
  created_at: string;
}

const PAGE_SIZE = 50;

async function fetchAuditRows(): Promise<AuditRow[]> {
  // Bypass the generated Supabase type narrowing until Sergio regenerates
  // types after applying migration 20260513170000.
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        order: (col: string, opts: { ascending: boolean }) => {
          limit: (n: number) => Promise<{ data: AuditRow[] | null; error: unknown }>;
        };
      };
    };
  };
  const { data, error } = await client
    .from('super_admin_audit')
    .select('id, actor_user_id, action, target_table, target_id, payload, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  if (error) {
    console.error('AuditLog: failed to load rows', error);
    throw error;
  }
  return data ?? [];
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function payloadPreview(payload: unknown): { short: string; full: string } {
  if (payload === null || payload === undefined) {
    return { short: '—', full: '' };
  }
  const full =
    typeof payload === 'string' ? payload : JSON.stringify(payload);
  const short = full.length > 80 ? `${full.slice(0, 80)}…` : full;
  return { short, full };
}

export const AuditLog: React.FC = () => {
  const [page, setPage] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ['superadmin', 'audit', 'all'],
    queryFn: fetchAuditRows,
  });

  const rows = useMemo(() => data ?? [], [data]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visibleRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Auditoría</h1>
        <p className="text-sm text-gray-500 mt-1">
          Registro inmutable de todas las acciones del super admin.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Acciones recientes</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 py-4">
              No se pudo cargar la auditoría. Revisa la consola para más detalles.
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">Aún no hay acciones registradas.</p>
          ) : (
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Tabla objetivo</TableHead>
                    <TableHead>ID objetivo</TableHead>
                    <TableHead>Carga útil</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((row) => {
                    const preview = payloadPreview(row.payload);
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-xs text-gray-600">
                          {formatTimestamp(row.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{row.action}</TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {row.target_table ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 font-mono">
                          {row.target_id ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs text-gray-700 max-w-[24rem]">
                          {preview.full ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help">{preview.short}</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md break-words">
                                <pre className="whitespace-pre-wrap text-[11px]">
                                  {preview.full}
                                </pre>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span>{preview.short}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}

          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
              <span>
                Página {page + 1} de {totalPages} · {rows.length} registros
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
